import { Checkbox } from "../client/components/ui/checkbox";
import useTranslation from "../client/hooks/useTranslation";

/**
 * Cases à cocher des comptes et groupes analysés par Edgy.
 *
 * Distinct du compte de référence : celui-ci ne sert qu'à pré-remplir les règles
 * prop firm depuis UN compte, alors qu'une analyse peut couvrir N comptes.
 */

type Option = { id: string; name: string };

type TargetSelectorProps = {
  accounts: Option[];
  groups: Option[];
  selectedAccountIds: string[];
  selectedGroupIds: string[];
  onChangeAccounts: (ids: string[]) => void;
  onChangeGroups: (ids: string[]) => void;
};

function toggle(ids: string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
}

export default function TargetSelector({
  accounts,
  groups,
  selectedAccountIds,
  selectedGroupIds,
  onChangeAccounts,
  onChangeGroups,
}: TargetSelectorProps) {
  const t = useTranslation();
  const nothingSelected = selectedAccountIds.length === 0 && selectedGroupIds.length === 0;

  return (
    <div className="flex flex-col gap-3">
      <div>
        <span className="text-sm font-medium">{t.playbook.targetsLabel}</span>
        <p className="text-xs text-muted-foreground mt-0.5">{t.playbook.targetsDescription}</p>
      </div>

      {accounts.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-xs font-medium text-muted-foreground">{t.playbook.targetsAccounts}</span>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {accounts.map((a) => (
              <label key={a.id} className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={selectedAccountIds.includes(a.id)}
                  onCheckedChange={() => onChangeAccounts(toggle(selectedAccountIds, a.id))}
                />
                <span className="text-sm truncate">{a.name}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {groups.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-xs font-medium text-muted-foreground">{t.playbook.targetsGroups}</span>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {groups.map((g) => (
              <label key={g.id} className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={selectedGroupIds.includes(g.id)}
                  onCheckedChange={() => onChangeGroups(toggle(selectedGroupIds, g.id))}
                />
                <span className="text-sm truncate">{g.name}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {nothingSelected && <p className="text-xs text-muted-foreground">{t.playbook.targetsEmpty}</p>}
    </div>
  );
}
